import { useState } from "react";
import { CSS } from "../App";

// ─── Public page: /verify ─────────────────────────────────────────────────────
// Anyone with a signed PDF can check it is exactly as it was when signed. The file is fingerprinted (SHA-256)
// here in the browser and only the fingerprint is sent — the document itself never leaves the device.

const longTime = d => new Date(d).toLocaleString("en-GB", { timeZone: "Europe/London", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) + " (UK time)";

async function sha256OfFile(file) {
  const hash = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return [...new Uint8Array(hash)].map(b => b.toString(16).padStart(2, "0")).join("");
}

export function VerifyPage() {
  const [fileName, setFileName] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function check(file) {
    if (!file) return;
    setFileName(file.name); setResult(null); setError(""); setBusy(true);
    try {
      const r = await fetch(`/api/esign?action=verify&hash=${await sha256OfFile(file)}`);
      const d = await r.json();
      if (!r.ok || d.error) setError(d.error || "We couldn't check that file. Please try again.");
      else setResult(d);
    } catch {
      setError("We couldn't check that file. Please check your connection and try again.");
    }
    setBusy(false);
  }

  return (
    <>
      <style>{CSS}</style>
      <div style={{ minHeight: "100vh", background: "var(--mist)", padding: "24px 16px" }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ fontSize: 13, color: "var(--sub)", marginBottom: 4 }}>CamStorage e-signature</div>
          <h1 style={{ fontFamily: "var(--fh)", color: "var(--navy)", fontSize: 24, marginBottom: 16 }}>Check a signed document</h1>

          <div className="card"><div className="cb">
            <p className="tsub" style={{ marginBottom: 14 }}>
              Choose a signed PDF to check it hasn't been changed since it was signed. The file stays on your device —
              only its fingerprint is checked.
            </p>
            <input type="file" accept="application/pdf,.pdf" onChange={e => check(e.target.files?.[0])} />
            {busy && <p className="tsm" style={{ marginTop: 12 }}>Checking…</p>}
            {error && <div className="login-err" style={{ fontSize: 14, marginTop: 12 }}>{error}</div>}
          </div></div>

          {result?.match && (
            <div className="card" style={{ borderLeft: "4px solid var(--success)" }}><div className="cb">
              <h2 style={{ color: "var(--success)", margin: "0 0 8px" }}>✅ Genuine and unchanged</h2>
              <p className="tsub" style={{ marginBottom: 12 }}><strong>{fileName}</strong> is exactly the signed copy that was issued.</p>
              <div style={{ display: "grid", gap: 4, fontSize: 14 }}>
                <div><strong>Document:</strong> {result.title}</div>
                {result.businessName && <div><strong>Issued by:</strong> {result.businessName}</div>}
                <div><strong>Signed by:</strong> {result.signerName}, {longTime(result.signedAt)}</div>
                {result.businessSignerName && <div><strong>Signed for the business by:</strong> {result.businessSignerName}, {longTime(result.businessSignedAt)}</div>}
                <div className="tsm tsub"><strong>Reference:</strong> {result.reference}</div>
              </div>
            </div></div>
          )}

          {result && !result.match && (
            <div className="card" style={{ borderLeft: "4px solid #C0392B" }}><div className="cb">
              <h2 style={{ color: "#C0392B", margin: "0 0 8px" }}>❌ No match</h2>
              <p className="tsub">
                <strong>{fileName}</strong> isn't an unchanged signed copy. Either it has been changed since it was signed,
                or it wasn't signed with CamStorage. Even re-saving a PDF in another program changes it — the original copy is
                the one attached to the "Signed" email.
              </p>
            </div></div>
          )}
        </div>
      </div>
    </>
  );
}
